// ---- Cost-row tooltip catalogue ----
// Source of truth: medicys-cost-row-tooltips.xlsx (Medicys-authored).
// Matching: each entry's `match` array is a list of lowercase keywords;
// a cost-row label hits an explainer when the lowercased label contains
// ANY of its keywords. Specificity wins, for ties on "Incentives
// handling fee" vs "Incentives", we pick the LONGEST matching keyword
// so the more specific tooltip surfaces.
//
// Text format: plain paragraphs separated by `\n`. A line beginning
// with `See:` followed by a URL gets rendered as a "Read more →" link
// in the tooltip (handled by `CostRowInfo`).

export const COST_ROW_EXPLAINERS = [
  // ---- Audience & fieldwork ----
  {
    label: "Sample (n)",
    match: ["sample (n)"],
    text: "The number of respondents we will recruit for this audience.",
  },
  {
    label: "Recruitment",
    match: ["recruitment"],
    text: "Identifying, screening, consenting and confirming the participant.",
  },
  {
    label: "Incentives task",
    match: ["incentives task", "incentive task", "incentives tasks"],
    text:
      "Additional honoraria for tasks completed outside the main interview.",
  },
  {
    label: "Incentives handling fee",
    match: ["incentives handling fee", "handling fee"],
    text:
      "Banking and processing surcharge for transferring incentives to respondents across countries and currencies.",
  },
  {
    label: "Incentives",
    match: ["incentives", "incentive"],
    text:
      "Max honoraria offered to respondent in recognition of their time.",
  },
  {
    label: "Project Management",
    match: ["project management", "project mgmt", "pm"],
    text:
      "Coordinating each respondent end-to-end: scheduling, briefing, dropout management and quota tracking.",
  },
  {
    label: "Cost per interview (CPI)",
    match: ["cost per interview", "cpi"],
    text:
      "All-in fee per respondent, bundling recruitment, incentives and project management into a single rate.",
  },

  // ---- Services ----
  {
    label: "Moderation",
    match: ["moderation"],
    text:
      "Live facilitation by an experienced moderator briefed on the therapy area and study objectives.",
  },
  {
    label: "Content analyses",
    match: ["content analyses", "content analysis"],
    text:
      "Thematic and verbatim analysis of the fieldwork outputs, surfacing the patterns and quotes that feed the deliverable.",
  },
  {
    label: "Translations",
    match: ["translation", "translations"],
    text:
      "See: https://medicysltd.co.uk/api/wp-content/uploads/2026/01/Medicys-Translation-Quality-Framework.pdf",
  },
  {
    label: "Verbatim transcripts",
    match: ["transcript", "transcripts", "transcription", "verbatim"],
    text:
      "See: https://medicysltd.co.uk/api/wp-content/uploads/2026/01/Medicys-Transcription-Quality-Framework.pdf",
  },
  {
    label: "Programming",
    match: ["programming"],
    text:
      "Build of any quantitative or hybrid survey; logic, routing, quotas, device testing.",
  },
  {
    label: "Conference platform",
    match: ["conference platform", "online platform", "web platform"],
    text:
      "Web-conferencing licence and technical support for hosting online groups or depths (breakouts, recording, observer rooms).",
  },
  {
    label: "Simultaneous translation",
    match: ["simultaneous translation", "sim trans", "simtrans"],
    text:
      "Live interpreter providing in-ear translation of the conversation for observers in real time.",
  },
  {
    label: "Note-taking",
    match: ["note taking", "note-taking", "notetaking"],
    text:
      "Dedicated note-taker capturing themes, observations and verbatims during the session.",
  },

  // ---- Setup & admin ----
  // Specific FIRST so "Study management" doesn't get swallowed by
  // "Setup/Admin & study management" via the longest-match rule.
  {
    label: "Study management (weekly / monthly)",
    match: [
      "weekly study management",
      "monthly study management",
      "study management",
    ],
    text:
      "Recurring fee for active management of the project across the fieldwork window.",
  },
  {
    label: "Setup/Admin & study management",
    match: [
      "setup/admin & study",
      "setup admin & study",
      "setup & admin",
      "setup/admin",
      "setup admin",
    ],
    text: "Project setup, documentation and ongoing oversight.",
  },
];

/**
 * Resolve a row label to its explainer entry (or null).
 * Longest matching keyword wins so more specific labels take priority.
 */
export function lookupExplainer(label) {
  if (!label) return null;
  const haystack = String(label).toLowerCase();
  let best = null;
  let bestLen = 0;
  for (const entry of COST_ROW_EXPLAINERS) {
    for (const kw of entry.match) {
      if (haystack.includes(kw) && kw.length > bestLen) {
        best = entry;
        bestLen = kw.length;
        break;
      }
    }
  }
  return best;
}

/**
 * Split a tooltip text into a body + optional link. A line that starts
 * with `See:` followed by a URL becomes the link; everything else is
 * the body.
 */
export function parseExplainerText(text) {
  if (!text) return { body: "", link: null };
  const m = String(text).match(/^See:\s*(https?:\/\/\S+)\s*$/i);
  if (m) return { body: "", link: m[1] };
  // Inline form: "Body sentence. See: https://..."
  const m2 = String(text).match(/^(.+?)\s+See:\s*(https?:\/\/\S+)\s*$/i);
  if (m2) return { body: m2[1].trim(), link: m2[2] };
  return { body: String(text).trim(), link: null };
}
